/**
 * CheckInPage.js
 *
 * /fly/check-in — online check-in for Flight YJ030, no account needed. Full-page on every screen.
 *   Entry (not a step): PNR (prefilled from ?pnr=) and last name, next to a Right Brother Airways jet
 *   1. Briefing: what check-in involves, so guests pay attention to the game and the voice note
 *   2. Your seat: the cabin's seat map with the guest's seat (assigned, not changeable)
 *   3. Customs declaration: two truths and a lie, for the party game (editable until 15:00 IST on the day)
 *   4. Black box (optional): a voice message for the birthday boy
 *   5. Boarding pass: checked in, and the Wallet button for this device (Apple on iPhone, Google on Android)
 *
 * Continue sits at the bottom of the screen (fixed to the bottom on phones) and only appears once a step is done.
 * Only guests who RSVPed "coming" can check in; anyone else is sent to their invite (/fly/<pnr>) to confirm first.
 * The backend remembers a successful check-in in an HttpOnly cookie, so coming back skips the entry screen.
 * The test booking (PNR TEST30, last name Passenger) goes through the same steps, but the backend saves
 * nothing for it: its answers are cached in this tab (sessionStorage) instead.
 */

import React, { useEffect, useLayoutEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Armchair, ArrowRight, CircleAlert, CircleCheckBig, Clock, DoorOpen, FileText, Mic, Plane, Search, TriangleAlert, Wallet } from "lucide-react";
import toast from "react-hot-toast";
import PlaneArt from "./components/PlaneArt";
import SeatMap from "./components/SeatMap";
import VoiceMemo from "./components/VoiceMemo";
import {
    applePassUrl, checkIn, getCheckedInBooking, getCheckInSession, getGooglePass, GOOGLE_WALLET_READY, PNR_PATTERN, saveDeclarations,
} from "./helpers/flyApi";
import { flyPath, useNoIndex } from "./helpers/flyPage";
import CheckInCountdown from "./components/CheckInCountdown";
import { useBoardingPassPdf } from "./components/BoardingPassPdf";
import appleWalletBadge from "./assets/add-to-apple-wallet.svg";
import googleWalletBadge from "./assets/add-to-google-wallet.svg";

const PAPER = "#FBF7EF";
const INK = "#1B2A41";
const MUTED = "#5B6577";
const LINE = "#C9C2B4";
const ERROR = "#A8183D";
const BACKDROP = "#E9E4DA";
const SKY = "linear-gradient(180deg, #9CC7E8 0%, #CFE4F2 55%, #F3E9D9 100%)";
const MONO = "'JetBrains Mono', monospace";
const SANS = "'Space Grotesk', system-ui, sans-serif";

const MAX_STATEMENT_LENGTH = 140;
const TEST_CACHE_KEY = "yj030-test-booking";

// The declaration form: two truths, then the lie (sent as statements [truth, truth, lie] with lie: 2)
const DECLARATION_FIELDS = [
    { label: "True statement 1", placeholder: "e.g. I've been to 14 countries" },
    { label: "True statement 2", placeholder: "e.g. I once got lost in an airport for 6 hours" },
    { label: "Lie", placeholder: "e.g. I've never missed a flight" },
];
const LIE_INDEX = 2;

// What check-in involves, shown on the briefing step
const CHECK_IN_PARTS = [
    { icon: Armchair,       title: "Your seat",             text: "See where you're sitting on the plane." },
    { icon: FileText,       title: "Customs declaration",   text: "For a game: write two truths and a lie about yourself. At the party everyone guesses whose card it is and which one's the lie, so make them spicy and fun." },
    { icon: Mic,            title: "Black box (optional)",  text: "Record a voice message for the birthday boy. Don't say your name: he has to guess who it's from." },
];

const STEPS = [
    { id: "briefing",   label: "Briefing" },
    { id: "seat",       label: "Seat" },
    { id: "customs",    label: "Customs" },
    { id: "memo",       label: "Black box" },
    { id: "done",       label: "Boarding pass" },
];

const inputClass = "w-full rounded-xl border px-4 py-3 bg-white text-base focus:outline-none focus:ring-2";

/**
 * The test booking as last saved in this tab, merged into what the backend sent (which never has the test's answers)
 */
const withTestCache = (booking) => {
    if (!booking.test) return booking;
    try {
        const cached = JSON.parse(sessionStorage.getItem(TEST_CACHE_KEY) || "null");
        if (!cached) return booking;
        const { declarations, declarationsSubmittedAt, checkedInAt, rsvp, voiceMemoAt } = cached;
        return { ...booking, declarations, declarationsSubmittedAt, checkedInAt, rsvp, voiceMemoAt };
    } catch {
        return booking;
    }
};

/**
 * Remembers the test booking's answers until this tab is closed
 */
const cacheTestBooking = (booking) => {
    if (!booking.test) return;
    try {
        sessionStorage.setItem(TEST_CACHE_KEY, JSON.stringify(booking));
    } catch {
        // Private mode: the test just won't survive a refresh
    }
};

/**
 * The form's three fields from a saved declaration: the two truths in order, then the lie
 */
const declarationFields = (declarations) => {
    if (!declarations) return ["", "", ""];
    const { statements, lie } = declarations;
    return [...statements.filter((_, index) => index !== lie), statements[lie]];
};

/**
 * Whether the customs declaration can still be written or changed (until 15:00 IST on the day, so the cards can be printed)
 */
const declarationsOpen = (booking) => booking.declarationsOpen ?? booking.checkInOpen;

/**
 * Which Wallet this device uses: ['apple'] on iPhone and iPad, ['google'] on Android, both elsewhere
 */
// Until Google approves the Wallet issuer (GOOGLE_WALLET_READY in flyApi.js), the Google Wallet button saves the
// boarding pass as a PDF instead, with a QR code the counter scans.

const walletsForDevice = () => {
    const agent = navigator.userAgent || "";
    // iPadOS reports itself as a Mac, but with touch
    if (/iPhone|iPad|iPod/.test(agent) || (/Macintosh/.test(agent) && navigator.maxTouchPoints > 1)) return ["apple"];
    if (/Android/i.test(agent)) return ["google"];
    return ["apple", "google"];
};

/**
 * Whether the screen is wide (computers and tablets in landscape)
 * @param {Number} width    -   Breakpoint in px
 */
const useWide = (width) => {
    const [wide, setWide] = useState(() => window.innerWidth >= width);
    useLayoutEffect(() => {
        const update = () => setWide(window.innerWidth >= width);
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [width]);
    return wide;
};

const ErrorMessage = ({ children }) => (
    <p role="alert" className="flex gap-2 text-sm" style={{ color: ERROR }}>
        <CircleAlert size={18} className="shrink-0" aria-hidden="true" /> <span>{children}</span>
    </p>
);

/**
 * Continue, at the bottom of the screen: fixed to the bottom of the view on phones, at the foot of the page on computers.
 * Only shown once the step is done.
 */
const ContinueBar = ({ visible, accent, label = "Continue", onClick, busy }) => {
    if (!visible) return null;
    return (
        <div
            className="fixed inset-x-0 bottom-0 z-30 lg:sticky lg:mt-auto"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
            <div className="flex flex-col gap-2 px-4 pt-3 pb-4 lg:px-0 lg:pt-0 lg:pb-0 lg:flex-row lg:items-center lg:justify-end lg:gap-6 border-t lg:border-0 bg-[#FBF7EF] lg:bg-[#E9E4DA] lg:pt-6 lg:pb-8" style={{ borderColor: LINE }}>
                <button
                    type="button"
                    onClick={onClick}
                    disabled={busy}
                    className="w-full lg:w-auto inline-flex items-center justify-center gap-2 rounded-full px-10 py-4 text-sm font-bold tracking-[0.2em] uppercase transition hover:opacity-90 disabled:opacity-60"
                    style={{ background: accent, color: PAPER, boxShadow: "0 8px 20px rgba(27,42,65,0.22)" }}
                >
                    {busy ? "One moment…" : label} {!busy && <ArrowRight size={16} aria-hidden="true" />}
                </button>
            </div>
        </div>
    );
};

/**
 * Only guests who RSVPed "coming" can check in: everyone else is sent to their invite to confirm first
 * @param {String} pnr      -   Their PNR, for the link to their invite
 */
const NotConfirmed = ({ pnr }) => (
    <>
        Sorry, your ticket isn't confirmed yet. Please visit{" "}
        <Link to={flyPath(`/fly/${pnr}`)} className="font-bold underline underline-offset-2">isayyashtothat.com{flyPath(`/fly/${pnr}`)}</Link>{" "}
        to confirm your ticket.
    </>
);

/**
 * The entry screen (not a step): PNR and last name on the left, the plane on the right (on top, on phones)
 * @param {String} initialPnr       -   From ?pnr=, still editable
 * @param {String} notConfirmedPnr  -   Set when a returning browser's booking isn't confirmed (any more), to say so straight away
 * @param {Function} onFound        -   Called with the booking
 */
const EntryScreen = ({ initialPnr, notConfirmedPnr, onFound }) => {
    const [pnr, setPnr] = useState(initialPnr);
    const [lastName, setLastName] = useState("");
    const [error, setError] = useState(() => (notConfirmedPnr ? <NotConfirmed pnr={notConfirmedPnr} /> : ""));
    const [checking, setChecking] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const cleanPnr = pnr.trim().toUpperCase();
        if (!PNR_PATTERN.test(cleanPnr) || !lastName.trim()) {
            setError("Enter your 6-character PNR and your last name, as shown on your invite.");
            return;
        }
        setError("");
        setChecking(true);
        try {
            onFound(await checkIn(cleanPnr, lastName));
        } catch (err) {
            if (err.status === 403) setError(<NotConfirmed pnr={cleanPnr} />);
            else setError(err.status === 404 || err.status === 429 ? err.message : "Something went wrong. Please try again.");
            setChecking(false);
        }
    };

    return (
        <div className="min-h-[100dvh] w-full text-left grid grid-rows-[auto_1fr] lg:grid-rows-1 lg:grid-cols-[minmax(420px,5fr)_7fr]" style={{ background: PAPER, color: INK, fontFamily: SANS }}>
            {/* The plane: a banner on phones, the whole right half on computers */}
            <div className="relative overflow-hidden h-[34vh] min-h-[200px] lg:h-auto lg:order-2 flex items-center justify-center" style={{ background: SKY }}>
                <PlaneArt className="w-[118%] max-w-none lg:w-[92%] lg:max-w-[980px] -translate-x-[4%] lg:translate-x-0" />
                <p className="hidden lg:block absolute bottom-8 left-0 right-0 text-center text-xs font-bold tracking-[0.3em]" style={{ color: INK }}>
                    FLIGHT YJ030 · BAH · BOARDING 20:00 · 30 OCT
                </p>
            </div>

            <form onSubmit={handleSubmit} className="lg:order-1 flex flex-col justify-center gap-6 px-6 py-8 sm:px-12 lg:px-16 xl:px-24">
                <div className="flex flex-col gap-3">
                    <p className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.28em]" style={{ color: MUTED }}>
                        <Plane size={14} aria-hidden="true" /> RIGHT BROTHER AIRWAYS
                    </p>
                    <h1 className="text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tight">Online check-in</h1>
                    <p className="text-base" style={{ color: MUTED }}>
                        Flight YJ030 to Bahubali Enclave. Boarding 8:00 PM, Friday 30 October.
                    </p>
                    <CheckInCountdown className="self-start" />
                </div>

                <div className="flex flex-col gap-4 max-w-md">
                    <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>PNR / BOOKING CODE</span>
                        <input
                            type="text"
                            value={pnr}
                            onChange={(e) => setPnr(e.target.value.toUpperCase())}
                            placeholder="e.g. CF38US"
                            maxLength={6}
                            autoCapitalize="characters"
                            autoCorrect="off"
                            autoComplete="off"
                            spellCheck={false}
                            className={`${inputClass} tracking-[0.35em] font-bold`}
                            style={{ borderColor: LINE, fontFamily: MONO, "--tw-ring-color": INK }}
                        />
                        <span className="text-xs" style={{ color: MUTED }}>The 6-character code on your boarding pass and in your invite link.</span>
                    </label>
                    <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>LAST NAME</span>
                        <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="As on your boarding pass"
                            maxLength={100}
                            autoComplete="family-name"
                            className={inputClass}
                            style={{ borderColor: LINE, "--tw-ring-color": INK }}
                        />
                    </label>
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                    <button
                        type="submit"
                        disabled={checking}
                        className="mt-1 inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-bold tracking-[0.2em] transition hover:opacity-90 disabled:opacity-60"
                        style={{ background: INK, color: PAPER }}
                    >
                        <Search size={16} aria-hidden="true" /> {checking ? "FINDING YOUR BOOKING…" : "FIND MY BOOKING"}
                    </button>
                </div>
            </form>
        </div>
    );
};

/**
 * The top of every step: "Checking-in to your flight to Bahubali Enclave" and where the guest is
 */
const FlowHeader = ({ step, booking, onStartOver }) => {
    const accent = booking.pass.colour;
    const index = STEPS.findIndex((item) => item.id === step);
    return (
        <header className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
                <p className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.28em]" style={{ color: MUTED }}>
                    <Plane size={14} aria-hidden="true" /> RIGHT BROTHER AIRWAYS<span className="hidden sm:inline"> · YJ030</span>
                </p>
                <button type="button" onClick={onStartOver} className="text-xs underline underline-offset-2 text-right" style={{ color: MUTED }}>
                    Not {booking.pass.fullName.split(" ")[0]}?
                </button>
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold leading-tight tracking-tight">
                Checking-in to your flight to <span style={{ color: accent }}>Bahubali Enclave</span>
            </h1>
            {/* Only while it matters: not once they've finished (done) */}
            {step !== "done" && <CheckInCountdown accent={accent} className="self-start" />}
            <ol className="grid grid-cols-5 gap-1.5 sm:gap-2" aria-label="Check-in steps">
                {STEPS.map((item, i) => (
                    <li key={item.id} className="flex flex-col gap-1.5" aria-current={i === index ? "step" : undefined}>
                        <span className="h-1.5 rounded-full" style={{ background: i <= index ? accent : LINE }} />
                        <span className="text-[9px] sm:text-[11px] font-bold tracking-[0.1em] sm:tracking-[0.14em] uppercase" style={{ color: i === index ? INK : MUTED }}>{item.label}</span>
                    </li>
                ))}
            </ol>
            {booking.test && (
                <p className="self-start rounded-full px-3 py-1 text-[11px] font-bold tracking-[0.18em]" style={{ background: INK, color: PAPER }}>
                    TEST BOOKING · NOTHING IS SAVED
                </p>
            )}
        </header>
    );
};

/**
 * Step 1: the briefing. What check-in involves, as its own step so guests take in the game and the voice note.
 */
const BriefingStep = ({ booking, onContinue }) => {
    const accent = booking.pass.colour;
    return (
        <>
            <section className="flex flex-col gap-6 max-w-3xl">
                <div>
                    <p className="text-[11px] font-bold tracking-[0.24em]" style={{ color: accent }}>BEFORE YOU BOARD</p>
                    <h2 className="text-2xl sm:text-3xl font-bold leading-tight">Welcome aboard, {booking.pass.fullName.split(" ")[0]}. Here's your check-in.</h2>
                    <p className="pt-1" style={{ color: MUTED }}>It takes about 10 minutes.</p>
                </div>
                <ol className="flex flex-col gap-3">
                    {CHECK_IN_PARTS.map(({ icon: Icon, title, text }, index) => (
                        <li key={title} className="flex gap-4 rounded-2xl p-4 sm:p-5" style={{ background: "#FFFFFF", borderLeft: `6px solid ${accent}` }}>
                            <span className="h-11 w-11 shrink-0 rounded-full inline-flex items-center justify-center" style={{ background: `${accent}1a`, color: accent }}>
                                <Icon size={22} aria-hidden="true" />
                            </span>
                            <span className="flex flex-col gap-1">
                                <span className="text-[11px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>STEP {index + 1}</span>
                                <span className="text-lg font-bold">{title}</span>
                                <span>{text}</span>
                            </span>
                        </li>
                    ))}
                    <li className="flex gap-4 rounded-2xl p-4 sm:p-5" style={{ background: "#FFFFFF", borderLeft: `6px solid ${INK}` }}>
                        <span className="h-11 w-11 shrink-0 rounded-full inline-flex items-center justify-center" style={{ background: `${INK}14`, color: INK }}>
                            <Wallet size={22} aria-hidden="true" />
                        </span>
                        <span className="flex flex-col gap-1">
                            <span className="text-[11px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>LAST</span>
                            <span className="text-lg font-bold">Your boarding pass</span>
                            <span>Add it to your Wallet. It's scanned at the door.</span>
                        </span>
                    </li>
                </ol>
                <p style={{ color: MUTED }}>Press Continue to start.</p>
            </section>
            <ContinueBar visible accent={accent} onClick={onContinue} />
        </>
    );
};

/**
 * Step 2: the seat map, and what's on the ticket
 */
const SeatStep = ({ booking, onContinue }) => {
    const { pass } = booking;
    const sideways = useWide(1100);
    const fact = (label, value) => (
        <div className="flex flex-col">
            <span className="text-[10px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>{label}</span>
            <span className="text-lg font-bold" style={{ fontFamily: MONO }}>{value}</span>
        </div>
    );
    const ticket = (
        <div className={`rounded-2xl p-4 grid gap-4 ${sideways ? "grid-cols-5 items-end" : "grid-cols-2 sm:grid-cols-4"}`} style={{ background: "#FFFFFF", borderLeft: `6px solid ${pass.colour}` }}>
            <div className={`flex flex-col ${sideways ? "" : "col-span-2 sm:col-span-4"}`}>
                <span className="text-[10px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>PASSENGER</span>
                <span className="text-lg font-bold">{pass.fullName}</span>
            </div>
            {fact("FROM", `${pass.fromCode} ${pass.fromCity}`)}
            {fact("TO", "BAH")}
            {fact("BOARDING", "20:00")}
            {fact("GATE", "26")}
        </div>
    );
    const map = <SeatMap seat={pass.seat} colour={pass.colour} sideways={sideways} />;
    return (
        <>
            {/* Wide screens: the ticket, then the cabin on its side. Phones: the cabin first, so the seat is in view. */}
            <section className="flex flex-col gap-6">
                <div>
                    <h2 className="text-xl font-bold">Your seat is <span style={{ color: pass.colour }}>{pass.seat}</span></h2>
                    <p className="text-sm" style={{ color: MUTED }}>
                        The captain assigns every seat on YJ030, so it's reserved in your name and can't be changed.
                    </p>
                </div>
                {sideways ? <>{ticket}{map}</> : <>{map}{ticket}</>}
            </section>
            <ContinueBar visible accent={pass.colour} onClick={onContinue} />
        </>
    );
};

/**
 * Step 3: two truths and a lie, written in that order, for the party game
 */
const CustomsStep = ({ booking, onSaved, onSkip }) => {
    const accent = booking.pass.colour;
    const [statements, setStatements] = useState(() => declarationFields(booking.declarations));
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [closed, setClosed] = useState(!declarationsOpen(booking));
    const complete = statements.every((statement) => statement.trim());

    const update = (index, value) => setStatements(statements.map((statement, i) => (i === index ? value : statement)));

    const submit = async () => {
        setError("");
        setSaving(true);
        try {
            onSaved(await saveDeclarations(booking.pass.pnr, statements, LIE_INDEX));
        } catch (err) {
            if (err.status === 403) setClosed(true);
            else setError(err.message || "Couldn't save your declaration. Please try again.");
            setSaving(false);
        }
    };

    if (closed) {
        return (
            <>
                <section className="max-w-2xl flex flex-col gap-2">
                    <h2 className="text-xl font-bold">Customs is closed</h2>
                    <p style={{ color: MUTED }}>Customs declarations closed at 3:00 PM on 30 October, so the cards could be printed. Your boarding pass is still waiting for you.</p>
                </section>
                <ContinueBar visible accent={accent} onClick={onSkip} />
            </>
        );
    }

    return (
        <>
            <section className="grid gap-8 lg:grid-cols-[5fr_6fr] lg:items-start">
                <div className="flex flex-col gap-4">
                    <div>
                        <p className="text-[11px] font-bold tracking-[0.24em]" style={{ color: accent }}>CUSTOMS DECLARATION</p>
                        <h2 className="text-2xl font-bold leading-tight">Anything to declare? Two truths and a lie.</h2>
                    </div>
                    <p>
                        This isn't paperwork, it's a game. Every passenger declares three things about themselves:
                        two true, one made up.
                    </p>
                    <ol className="flex flex-col gap-3 text-sm">
                        {[
                            ["At the party", "All the declarations go up on the Unclaimed Declarations wall. No names, and each card's three statements are mixed up, so the lie could be anywhere."],
                            ["The game", "Everyone hunts for whose card is whose, and which statement is the lie."],
                            ["Make it good", "Pick things people wouldn't guess. The best cards are a believable lie hiding next to two surprising truths."],
                        ].map(([title, text], i) => (
                            <li key={title} className="flex gap-3">
                                <span className="h-7 w-7 shrink-0 rounded-full inline-flex items-center justify-center text-xs font-bold" style={{ background: accent, color: PAPER }}>{i + 1}</span>
                                <span><b>{title}.</b> {text}</span>
                            </li>
                        ))}
                    </ol>
                    <p className="text-xs" style={{ color: MUTED }}>You can change your answers until 3:00 PM on 30 October. After that, the cards go to print.</p>
                </div>

                <div className="flex flex-col gap-4 rounded-2xl p-5 sm:p-6" style={{ background: "#FFFFFF" }}>
                    {DECLARATION_FIELDS.map((field, index) => (
                        <label key={field.label} className="flex flex-col gap-1.5">
                            <span className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: index === LIE_INDEX ? ERROR : MUTED }}>{field.label}</span>
                            <textarea
                                value={statements[index]}
                                onChange={(e) => update(index, e.target.value)}
                                maxLength={MAX_STATEMENT_LENGTH}
                                rows={2}
                                placeholder={field.placeholder}
                                className={`${inputClass} resize-none`}
                                style={{ borderColor: index === LIE_INDEX ? ERROR : LINE, "--tw-ring-color": accent }}
                            />
                            <span className="self-end text-[11px]" style={{ color: MUTED, fontFamily: MONO }}>{statements[index].length}/{MAX_STATEMENT_LENGTH}</span>
                        </label>
                    ))}
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </div>
            </section>
            <ContinueBar visible={complete} accent={accent} label="Complete check-in" onClick={submit} busy={saving} />
        </>
    );
};

/**
 * Step 4 (optional): a voice message for the birthday boy
 */
const MemoStep = ({ booking, onSaved, onContinue }) => {
    const accent = booking.pass.colour;
    return (
        <>
            <section className="grid gap-8 lg:grid-cols-2 lg:items-center">
                <div className="flex flex-col gap-4">
                    <div>
                        <p className="text-[11px] font-bold tracking-[0.24em]" style={{ color: accent }}>OPTIONAL · COCKPIT VOICE RECORDER</p>
                        <h2 className="text-2xl sm:text-3xl font-bold leading-tight">Leave a message in the black box</h2>
                    </div>
                    <p>
                        Every flight carries a black box. Ours records birthday messages. Leave the birthday boy a voice note:
                        a wish, a memory, a story he'd rather forget.
                    </p>
                    <p className="rounded-xl px-4 py-3 text-sm" style={{ background: `${accent}14`, borderLeft: `4px solid ${accent}` }}>
                        <b>Don't say your name.</b> The black box is opened after the flight, and Yash has to guess who's speaking.
                    </p>
                </div>
                <div className="rounded-2xl px-5 py-10 sm:px-8" style={{ background: "#FFFFFF" }}>
                    {booking.checkInOpen || booking.voiceMemoAt
                        ? <VoiceMemo booking={booking} onSaved={onSaved} />
                        : <p className="text-center" style={{ color: MUTED }}>The black box closed at 8:00 PM on 30 October.</p>}
                </div>
            </section>
            {booking.voiceMemoAt || !booking.checkInOpen ? (
                <ContinueBar visible accent={accent} onClick={onContinue} />
            ) : (
                <div className="mt-8 lg:mt-auto lg:pt-8 flex justify-center lg:justify-end">
                    <button type="button" onClick={onContinue} className="text-sm font-bold tracking-[0.14em] underline underline-offset-4" style={{ color: MUTED }}>
                        SKIP FOR NOW
                    </button>
                </div>
            )}
        </>
    );
};

/**
 * "Add to Apple Wallet" (a normal link to the .pkpass) and/or "Add to Google Wallet" (a link from the backend,
 * or for now the boarding pass as a PDF: see GOOGLE_WALLET_READY), for this device
 * @param {Object} pass     -   The guest's boarding pass
 */
const WalletButtons = ({ pass }) => {
    const { pnr } = pass;
    const [wallets] = useState(walletsForDevice);
    const [loadingGoogle, setLoadingGoogle] = useState(false);
    const pdf = useBoardingPassPdf(pass);

    const addToGoogle = async () => {
        setLoadingGoogle(true);
        if (!GOOGLE_WALLET_READY) {
            const saved = await pdf.download();
            if (saved) toast.success("Boarding pass saved. Keep it on your phone and show its QR code at the door.", { duration: 6000 });
            else toast.error("Couldn't make your boarding pass. Please try again.");
            setLoadingGoogle(false);
            return;
        }
        try {
            const { saveUrl } = await getGooglePass(pnr);
            window.location.href = saveUrl;
        } catch (err) {
            toast.error(err.message || "Couldn't make your Google Wallet pass.");
            setLoadingGoogle(false);
        }
    };

    // Apple's and Google's own badges, shown as they are, at the same height
    const badge = "inline-flex rounded-[14px] transition hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 disabled:opacity-60";
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
                {wallets.includes("apple") && (
                    <a href={applePassUrl(pnr)} className={badge}>
                        <img src={appleWalletBadge} alt="Add to Apple Wallet" className="h-14 w-auto" draggable={false} />
                    </a>
                )}
                {wallets.includes("google") && (
                    <button type="button" onClick={addToGoogle} disabled={loadingGoogle} aria-busy={loadingGoogle} className={badge}>
                        <img src={googleWalletBadge} alt="Add to Google Wallet" className="h-14 w-auto" draggable={false} />
                    </button>
                )}
            </div>
            {loadingGoogle && <p role="status" className="text-xs" style={{ color: MUTED }}>{GOOGLE_WALLET_READY ? "Opening Google Wallet…" : "Making your boarding pass…"}</p>}
            {pdf.drawing}
        </div>
    );
};

/**
 * Step 5: checked in, and the Wallet pass (very important: it's scanned at the door).
 * Phones: the headline, then the Wallet card, then the details. Wide screens: details on the left, Wallet on the right.
 */
const DoneStep = ({ booking, onEdit }) => {
    const { pass } = booking;
    const points = [
        { icon: CircleCheckBig, text: booking.checkedInAt ? "You're checked in for YJ030" : "Flight YJ030 to Bahubali Enclave" },
        { icon: Armchair, text: `Seat ${pass.seat}` },
        { icon: Clock, text: "Boarding 20:00, 30 Oct" },
        { icon: DoorOpen, text: "Gate 26" },
    ];
    return (
        <section className="grid gap-6 lg:gap-x-10 lg:grid-cols-2 lg:items-start">
            <div className="flex flex-col gap-3 lg:col-start-1 lg:row-start-1">
                <h2 className="flex items-center gap-3 text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tight">
                    <CircleCheckBig className="shrink-0 h-10 w-10 sm:h-12 sm:w-12" color={pass.colour} aria-hidden="true" />
                    {booking.checkedInAt ? "Check-in successful" : "Your boarding pass"}
                </h2>
            </div>

            <div className="rounded-2xl p-5 sm:p-7 flex flex-col gap-4 lg:col-start-2 lg:row-start-1 lg:row-span-2" style={{ background: "#FFFFFF", border: `3px solid ${pass.colour}`, boxShadow: "0 12px 30px rgba(27,42,65,0.14)" }}>
                <p className="self-start inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-bold tracking-[0.2em]" style={{ background: ERROR, color: PAPER }}>
                    <TriangleAlert size={14} aria-hidden="true" /> VERY IMPORTANT FOR THE FLIGHT
                </p>
                <h3 className="text-xl font-bold">Add your boarding pass to your Wallet</h3>
                <p className="text-sm" style={{ color: MUTED }}>
                    Ground staff scan the QR code on your boarding pass at the door. It's how you board the flight, so add it now and keep it on your phone.
                </p>
                <WalletButtons pass={pass} />
            </div>

            <div className="flex flex-col gap-5 lg:col-start-1 lg:row-start-2">
                <ul className="flex flex-col gap-3">
                    {points.map(({ icon: Icon, text }) => (
                        <li key={text} className="flex items-center gap-3 text-lg sm:text-xl font-bold">
                            <span className="h-10 w-10 shrink-0 rounded-full inline-flex items-center justify-center" style={{ background: `${pass.colour}1a`, color: pass.colour }}>
                                <Icon size={20} aria-hidden="true" />
                            </span>
                            {text}
                        </li>
                    ))}
                </ul>
                <p style={{ color: MUTED }}>
                    {booking.voiceMemoAt ? "Your message is in the black box. " : ""}See you at the gate, {pass.fullName.split(" ")[0]}.
                </p>
                {/* One link per line */}
                <div className="flex flex-col items-start gap-3 text-xs font-bold tracking-[0.16em]">
                    {declarationsOpen(booking) && <button type="button" onClick={() => onEdit("customs")} className="underline underline-offset-4">EDIT CUSTOMS DECLARATION</button>}
                    {booking.checkInOpen && <button type="button" onClick={() => onEdit("memo")} className="underline underline-offset-4">{booking.voiceMemoAt ? "BLACK BOX MESSAGE" : "RECORD A BLACK BOX MESSAGE"}</button>}
                    <Link to={flyPath(`/fly/${pass.pnr}`)} className="underline underline-offset-4">VIEW YOUR INVITE</Link>
                </div>
            </div>
        </section>
    );
};

const CheckInPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryPnr = (searchParams.get("pnr") || "").trim().toUpperCase();
    // 'loading' | 'find' | 'seat' | 'customs' | 'memo' | 'done'
    const [step, setStep] = useState("loading");
    const [booking, setBooking] = useState(null);
    // A returning browser whose booking isn't confirmed (any more): the entry screen says so straight away
    const [notConfirmedPnr, setNotConfirmedPnr] = useState(null);

    useNoIndex("Check-in · Flight YJ030");

    // Coming back? Skip the entry screen if this browser already checked in with this PNR (or with one, if there's no ?pnr=)
    useEffect(() => {
        let ignore = false;
        const resume = async () => {
            try {
                const { pnrs } = await getCheckInSession();
                const pnr = queryPnr ? (pnrs.includes(queryPnr) ? queryPnr : null) : pnrs[pnrs.length - 1];
                if (!pnr) {
                    if (!ignore) setStep("find");
                    return;
                }
                let found;
                try {
                    found = withTestCache(await getCheckedInBooking(pnr));
                } catch (err) {
                    if (!ignore && err.status === 403) setNotConfirmedPnr(pnr);
                    throw err;
                }
                if (ignore) return;
                setBooking(found);
                setStep(found.declarationsSubmittedAt ? "done" : "briefing");
            } catch {
                if (!ignore) setStep("find");
            }
        };
        resume();
        return () => { ignore = true; };
        // Only on arrival: later ?pnr= changes come from this page itself
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Each step starts at the top, like a new page
    useEffect(() => { window.scrollTo(0, 0); }, [step]);

    const update = (newBooking) => {
        cacheTestBooking(newBooking);
        setBooking(newBooking);
    };

    const found = (foundBooking) => {
        const newBooking = withTestCache(foundBooking);
        setBooking(newBooking);
        setSearchParams({ pnr: newBooking.pass.pnr }, { replace: true });
        setStep("briefing");
    };

    const startOver = () => {
        setBooking(null);
        setSearchParams({}, { replace: true });
        setStep("find");
    };

    if (step === "loading") {
        return (
            <div className="min-h-[100dvh] flex items-center justify-center" style={{ background: PAPER, fontFamily: SANS }}>
                <p className="text-sm font-bold tracking-[0.24em] animate-pulse" style={{ color: MUTED }}>CHECKING YOUR BOOKING…</p>
            </div>
        );
    }

    if (step === "find") return <EntryScreen initialPnr={queryPnr || notConfirmedPnr || ""} notConfirmedPnr={notConfirmedPnr} onFound={found} />;

    return (
        <div className="min-h-[100dvh] w-full text-left flex flex-col" style={{ background: BACKDROP, color: INK, fontFamily: SANS }}>
            {/* Room at the bottom on phones for the fixed Continue bar */}
            <main className="flex-1 w-full max-w-6xl mx-auto flex flex-col gap-8 px-5 pt-6 pb-36 sm:px-8 lg:px-12 lg:pt-10 lg:pb-10">
                <FlowHeader step={step} booking={booking} onStartOver={startOver} />
                <div className="flex-1 flex flex-col">
                    {step === "briefing" && <BriefingStep booking={booking} onContinue={() => setStep("seat")} />}
                    {step === "seat" && <SeatStep booking={booking} onContinue={() => setStep("customs")} />}
                    {step === "customs" && (
                        <CustomsStep
                            booking={booking}
                            onSaved={(saved) => { update(saved); setStep("memo"); }}
                            onSkip={() => setStep("memo")}
                        />
                    )}
                    {step === "memo" && <MemoStep booking={booking} onSaved={update} onContinue={() => setStep("done")} />}
                    {step === "done" && <DoneStep booking={booking} onEdit={setStep} />}
                </div>
            </main>
        </div>
    );
};

export default CheckInPage;
